import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../../../hooks/useAuth";
import {
  useProviderPricingItems,
  useSaveProviderPricingStep,
  useProviderOwnerOnboardingStatus,
  useSubmitProviderOnboardingForReview,
  canSubmitOnboardingForReview,
} from "../../provider-owner.hooks";
import type { ProviderPricingItemDto } from "../../provider-owner.types";

const SERVICE_CATEGORIES = [
  "Hair",
  "Barbering",
  "Makeup",
  "Nails",
  "Brows & lashes",
  "Skincare & facials",
  "Spa & body care",
  "Tattoo & body art",
  "Permanent makeup",
  "Piercing",
  "Aesthetics & cosmetic beauty",
] as const;

const SERVICE_SUBCATEGORIES: Record<string, readonly string[]> = {
  Hair: ["Haircut", "Styling", "Colouring", "Treatment", "Extensions"],
  Barbering: ["Haircut", "Beard trim", "Shave", "Fade", "Grooming package"],
  Makeup: ["Party makeup", "Bridal makeup", "Trial", "Editorial makeup"],
  Nails: ["Manicure", "Pedicure", "Gel nails", "Acrylic nails", "Nail art"],
  "Brows & lashes": [
    "Threading",
    "Tinting",
    "Brow lamination",
    "Lash lift",
    "Lash extensions",
  ],
  "Skincare & facials": ["Facial", "Peel", "Cleanup", "Dermaplaning"],
  "Spa & body care": ["Body polish", "Waxing", "Massage", "Body wrap"],
  "Tattoo & body art": [
    "Consultation",
    "Fine line",
    "Realism",
    "Cover-up",
    "Sleeve session",
    "Touch-up",
  ],
  "Permanent makeup": ["Brows", "Lips", "Eyeliner", "Correction", "Touch-up"],
  Piercing: [
    "Ear piercing",
    "Nose piercing",
    "Body piercing",
    "Jewellery change",
    "Aftercare consult",
  ],
  "Aesthetics & cosmetic beauty": [
    "Consultation",
    "Skin booster",
    "Chemical peel",
    "Laser beauty service",
    "Injectable consultation",
  ],
};

const COMPLIANCE_SENSITIVE_SERVICE_CATEGORIES = new Set<string>([
  "Tattoo & body art",
  "Permanent makeup",
  "Piercing",
  "Aesthetics & cosmetic beauty",
]);

type ServiceDraft = {
  id: string;
  persistedId?: string;
  serviceName: string;
  serviceCode: string;
  serviceCategory: string;
  serviceSubcategory: string;
  durationMinutes: string;
  price: string;
  depositRequired: boolean;
  depositAmount: string;
  status: "DRAFT" | "SUBMITTED";
  internalNotes: string;
};

const cardStyle: React.CSSProperties = {
  background: "#ffffff",
  border: "1px solid #e5e7eb",
  borderRadius: 16,
  padding: "1rem",
};

const inputStyle: React.CSSProperties = {
  width: "100%",
  border: "1px solid #d1d5db",
  borderRadius: 12,
  padding: "0.8rem 0.9rem",
  fontSize: "0.96rem",
  background: "#fff",
  color: "#0f172a",
};

const labelStyle: React.CSSProperties = {
  fontSize: "0.9rem",
  fontWeight: 600,
  color: "#334155",
};

const fieldWrapStyle: React.CSSProperties = {
  display: "grid",
  gap: "0.45rem",
};

const gridStyle: React.CSSProperties = {
  display: "grid",
  gap: "0.85rem",
  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
};

const primaryButtonStyle: React.CSSProperties = {
  border: "none",
  borderRadius: 12,
  background: "#111827",
  color: "#fff",
  padding: "0.8rem 1rem",
  fontWeight: 700,
  cursor: "pointer",
};

const secondaryButtonStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  textDecoration: "none",
  border: "1px solid #d1d5db",
  borderRadius: 12,
  background: "#fff",
  color: "#111827",
  padding: "0.8rem 1rem",
  fontWeight: 700,
};

const rowStyle: React.CSSProperties = {
  border: "1px solid #e5e7eb",
  borderRadius: 16,
  padding: "1rem",
  background: "#f8fafc",
};

const textareaStyle: React.CSSProperties = {
  ...inputStyle,
  minHeight: 96,
  resize: "vertical" as const,
};

const complianceHintStyle: React.CSSProperties = {
  marginTop: "0.9rem",
  padding: "0.85rem 0.95rem",
  borderRadius: 12,
  border: "1px solid #fde68a",
  background: "#fffbeb",
  color: "#92400e",
  fontSize: "0.92rem",
  lineHeight: 1.6,
};

const disabledButtonStyle: React.CSSProperties = {
  ...primaryButtonStyle,
  opacity: 0.5,
  cursor: "not-allowed",
};

const initialService = (): ServiceDraft => ({
  id: `service-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  serviceName: "",
  serviceCode: "",
  serviceCategory: "",
  serviceSubcategory: "",
  durationMinutes: "60",
  price: "",
  depositRequired: false,
  depositAmount: "",
  status: "DRAFT",
  internalNotes: "",
});

function mapPricingItemToDraft(item: ProviderPricingItemDto): ServiceDraft {
  return {
    id: `persisted-${item.id}`,
    persistedId: item.id,
    serviceName: item.serviceName,
    serviceCode: "",
    serviceCategory: item.category,
    serviceSubcategory: "",
    durationMinutes: String(item.durationMinutes ?? 60),
    price: String(item.price ?? ""),
    depositRequired: typeof item.depositAmount === "number" && item.depositAmount > 0,
    depositAmount:
      typeof item.depositAmount === "number" ? String(item.depositAmount) : "",
    status: item.isActive ? "SUBMITTED" : "DRAFT",
    internalNotes: "",
  };
}

export default function ProviderServicesStepPage() {
  const { user } = useAuth();
  const providerId = user?.providerId ?? "";

  const pricingQuery = useProviderPricingItems(providerId);
  const statusQuery = useProviderOwnerOnboardingStatus(providerId);
  const savePricingStep = useSaveProviderPricingStep(providerId);
  const submitForReview = useSubmitProviderOnboardingForReview();

  const [services, setServices] = useState<ServiceDraft[]>([initialService()]);
  const [savedAt, setSavedAt] = useState<string | null>(null);

  useEffect(() => {
    if (pricingQuery.data && pricingQuery.data.length > 0) {
      setServices(pricingQuery.data.map(mapPricingItemToDraft));
    }
  }, [pricingQuery.data]);

  const filledCount = useMemo(() => {
    return services.filter(
      (service) =>
        service.serviceName.trim() &&
        service.serviceCategory.trim() &&
        service.durationMinutes.trim() &&
        service.price.trim(),
    ).length;
  }, [services]);

  const updateService =
    <K extends keyof ServiceDraft>(id: string, key: K, value: ServiceDraft[K]) => {
      setServices((current) =>
        current.map((service) => {
          if (service.id !== id) return service;

          if (key === "serviceCategory") {
            return {
              ...service,
              serviceCategory: value as string,
              serviceSubcategory: "",
            };
          }

          return { ...service, [key]: value };
        }),
      );
    };

  const addService = () => {
    setServices((current) => [...current, initialService()]);
  };

  const removeService = (id: string) => {
    setServices((current) =>
      current.length === 1 ? current : current.filter((service) => service.id !== id),
    );
  };

  const handleSave = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!providerId) return;

    const readyServices = services.filter(
      (service) =>
        service.serviceName.trim() &&
        service.serviceCategory.trim() &&
        service.durationMinutes.trim() &&
        service.price.trim(),
    );

    for (const service of readyServices) {
      const payload = {
        id: service.persistedId,
        serviceName: service.serviceName.trim(),
        category: service.serviceCategory.trim(),
        price: Number(service.price),
        durationMinutes: Number(service.durationMinutes),
        depositAmount:
          service.depositRequired && service.depositAmount.trim()
            ? Number(service.depositAmount)
            : null,
        isActive: service.status === "SUBMITTED",
      };

      await savePricingStep.mutateAsync(payload);
    }

    setSavedAt(new Date().toLocaleTimeString());
  };

  const canSubmit =
    !!statusQuery.data && canSubmitOnboardingForReview(statusQuery.data);

  if (!providerId) {
    return (
      <div style={cardStyle}>
        <h2 style={{ margin: 0, color: "#0f172a" }}>Services and pricing</h2>
        <p style={{ marginTop: "0.5rem", color: "#b91c1c" }}>
          No provider is linked to this account yet. Please contact support.
        </p>
      </div>
    );
  }

  if (pricingQuery.isLoading || statusQuery.isLoading) {
    return (
      <div style={cardStyle}>
        <p style={{ margin: 0, color: "#475569" }}>
          Loading services and onboarding status...
        </p>
      </div>
    );
  }

  if (pricingQuery.isError || statusQuery.isError) {
    return (
      <div style={cardStyle}>
        <p style={{ margin: 0, color: "#b91c1c" }}>
          Unable to load services or onboarding status. Please refresh and try again.
        </p>
      </div>
    );
  }

  return (
    <div style={{ display: "grid", gap: "1rem" }}>
      <section style={cardStyle}>
        <h2 style={{ margin: 0, color: "#0f172a", fontSize: "1.4rem" }}>
          Services and pricing
        </h2>
        <p style={{ margin: "0.5rem 0 0", color: "#475569", lineHeight: 1.7 }}>
          Define the provider catalogue using HnB service categories, then name the
          actual services you want customers and reviewers to see.
        </p>
        <p style={{ margin: "0.75rem 0 0", color: "#0f172a", fontWeight: 700 }}>
          Ready service rows: {filledCount} / {services.length}
        </p>
      </section>

      <form onSubmit={handleSave} style={{ display: "grid", gap: "1rem" }}>
        {services.map((service, index) => {
          const subcategories =
            SERVICE_SUBCATEGORIES[service.serviceCategory] ?? [];
          const isComplianceSensitive =
            COMPLIANCE_SENSITIVE_SERVICE_CATEGORIES.has(service.serviceCategory);

          return (
            <section key={service.id} style={rowStyle}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: "0.75rem",
                  alignItems: "center",
                  marginBottom: "1rem",
                  flexWrap: "wrap",
                }}
              >
                <div>
                  <h3 style={{ margin: 0, color: "#0f172a" }}>
                    Service {index + 1}
                  </h3>
                  <p style={{ margin: "0.3rem 0 0", color: "#64748b" }}>
                    Add a categorized service definition with price and timing.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => removeService(service.id)}
                  style={{
                    border: "1px solid #fecaca",
                    background: "#fff1f2",
                    color: "#9f1239",
                    borderRadius: 10,
                    padding: "0.65rem 0.9rem",
                    fontWeight: 700,
                    cursor: "pointer",
                  }}
                >
                  Remove
                </button>
              </div>

              <div style={gridStyle}>
                <div style={fieldWrapStyle}>
                  <label style={labelStyle}>Service category</label>
                  <select
                    style={inputStyle}
                    value={service.serviceCategory}
                    onChange={(event) =>
                      updateService(service.id, "serviceCategory", event.target.value)
                    }
                  >
                    <option value="">Select category</option>
                    {SERVICE_CATEGORIES.map((category) => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                  </select>
                </div>

                <div style={fieldWrapStyle}>
                  <label style={labelStyle}>Service subcategory</label>
                  <select
                    style={inputStyle}
                    value={service.serviceSubcategory}
                    onChange={(event) =>
                      updateService(service.id, "serviceSubcategory", event.target.value)
                    }
                    disabled={!service.serviceCategory}
                  >
                    <option value="">
                      {service.serviceCategory
                        ? "Select subcategory"
                        : "Choose category first"}
                    </option>
                    {subcategories.map((subcategory) => (
                      <option key={subcategory} value={subcategory}>
                        {subcategory}
                      </option>
                    ))}
                  </select>
                </div>

                <div style={fieldWrapStyle}>
                  <label style={labelStyle}>Service name</label>
                  <input
                    style={inputStyle}
                    value={service.serviceName}
                    onChange={(event) =>
                      updateService(service.id, "serviceName", event.target.value)
                    }
                    placeholder="Fine line wrist tattoo, up to 5 cm"
                  />
                </div>

                <div style={fieldWrapStyle}>
                  <label style={labelStyle}>Service code</label>
                  <input
                    style={inputStyle}
                    value={service.serviceCode}
                    onChange={(event) =>
                      updateService(service.id, "serviceCode", event.target.value)
                    }
                    placeholder="HNB-SVC-001"
                  />
                </div>

                <div style={fieldWrapStyle}>
                  <label style={labelStyle}>Duration (minutes)</label>
                  <input
                    style={inputStyle}
                    type="number"
                    min={5}
                    step={5}
                    value={service.durationMinutes}
                    onChange={(event) =>
                      updateService(service.id, "durationMinutes", event.target.value)
                    }
                  />
                </div>

                <div style={fieldWrapStyle}>
                  <label style={labelStyle}>Price</label>
                  <input
                    style={inputStyle}
                    value={service.price}
                    onChange={(event) =>
                      updateService(service.id, "price", event.target.value)
                    }
                    placeholder="35.00"
                  />
                </div>

                <div style={fieldWrapStyle}>
                  <label style={labelStyle}>Status</label>
                  <select
                    style={inputStyle}
                    value={service.status}
                    onChange={(event) =>
                      updateService(
                        service.id,
                        "status",
                        event.target.value as ServiceDraft["status"],
                      )
                    }
                  >
                    <option value="DRAFT">DRAFT</option>
                    <option value="SUBMITTED">SUBMITTED</option>
                  </select>
                </div>
              </div>

              <div
                style={{
                  display: "grid",
                  gap: "0.85rem",
                  gridTemplateColumns: "1fr 220px",
                  marginTop: "1rem",
                }}
              >
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.6rem",
                    fontWeight: 600,
                    color: "#334155",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={service.depositRequired}
                    onChange={(event) =>
                      updateService(
                        service.id,
                        "depositRequired",
                        event.target.checked,
                      )
                    }
                  />
                  Deposit required
                </label>

                <div style={fieldWrapStyle}>
                  <label style={labelStyle}>Deposit amount</label>
                  <input
                    style={inputStyle}
                    value={service.depositAmount}
                    onChange={(event) =>
                      updateService(service.id, "depositAmount", event.target.value)
                    }
                    placeholder="10.00"
                    disabled={!service.depositRequired}
                  />
                </div>
              </div>

              <div style={{ ...fieldWrapStyle, marginTop: "1rem" }}>
                <label style={labelStyle}>Internal notes</label>
                <textarea
                  style={textareaStyle}
                  value={service.internalNotes}
                  onChange={(event) =>
                    updateService(service.id, "internalNotes", event.target.value)
                  }
                  placeholder="Add session notes, aftercare notes, consultation conditions, or operational guidance."
                />
              </div>

              {isComplianceSensitive ? (
                <div style={complianceHintStyle}>
                  This category is compliance-sensitive. Staff review may later require
                  additional verification, practitioner evidence, or policy checks before
                  activation.
                </div>
              ) : null}
            </section>
          );
        })}

        <section style={cardStyle}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: "0.75rem",
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            <div style={{ color: "#475569" }}>
              {savedAt ? `Draft saved at ${savedAt}` : "No onboarding services save yet"}
            </div>

            <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
              <button type="button" onClick={addService} style={secondaryButtonStyle}>
                Add another service
              </button>
              <button
                type="submit"
                style={savePricingStep.isPending ? disabledButtonStyle : primaryButtonStyle}
                disabled={savePricingStep.isPending}
              >
                {savePricingStep.isPending ? "Saving..." : "Save services"}
              </button>
              <Link to="/provider/onboarding/calendar" style={secondaryButtonStyle}>
                Continue to calendar
              </Link>
              <button
                type="button"
                style={canSubmit ? primaryButtonStyle : disabledButtonStyle}
                disabled={!canSubmit || submitForReview.isPending}
                onClick={() => submitForReview.mutate(providerId)}
              >
                {submitForReview.isPending ? "Submitting..." : "Submit for review"}
              </button>
            </div>
          </div>
        </section>
      </form>
    </div>
  );
}